/**
 * PATH: packages/auth/src/reload-watchdog/reloadCircuitBreaker.ts
 * FILE_ID: AUTH-RELOAD-WATCHDOG-BREAKER-001
 * PURPOSE: Stop runaway window.location.reload() loops by counting
 *          reload attempts inside a tight rolling window. Once the
 *          per-tab counter exceeds a threshold inside the window, the
 *          breaker trips: subsequent reload calls are suppressed and
 *          the caller is told to surface a banner + post a diagnostic
 *          instead.
 * WHY:     Tenant-SPA twin of `src/shared/reload-circuit-breaker.ts`
 *          (the master SPA's breaker). The master SPA installed its
 *          breaker against the "every-second-reload" family of bugs
 *          (BUG-2026-05-21-EVERY-SECOND-RELOAD-LOOP) and it has been
 *          load-bearing in production since 2026-05-21. The tenant
 *          SPAs (sam, newmark, steel, developer, workforce) had NO
 *          equivalent — they were importing reload triggers from
 *          `@quotech/tenant-shared/quotechApi` (silent-refresh
 *          identity mismatch, response header mismatch — both
 *          `setTimeout(reload, 150)`) without any rolling-window
 *          counter to break the loop. Operator complaint 2026-05-24:
 *          *"the job page refreshed like 12 times before stabalising
 *          after a refresh again.. this is a recurring problem
 *          pattern audit and fix it also put proper trails to see
 *          when the refresh happens so you understand the trigger
 *          leading to it"*. The 12 reloads are exactly the shape we
 *          would expect from a persistent identity mismatch (every
 *          API response on `/jobs` triggers another reload, the
 *          cookie/tab-pin alignment eventually catches up, loop
 *          self-terminates). This module + the install wrapper give
 *          every tenant SPA the same defence the master has had for
 *          three days.
 *
 *          Why a sibling file instead of importing the master's:
 *          `src/shared/` is the master SPA's private source tree —
 *          tenant SPAs (`apps/<tenant>/`) cannot import from
 *          `../../../src/shared` because of Vite resolution + TS
 *          paths. Lifting the master's file into `@quotech/auth`
 *          would force a synchronous refactor of `src/main.jsx`'s
 *          inline wrapper. That refactor is high-risk (master SPA
 *          handles login + signout for every tenant) and unrelated
 *          to the immediate fix. This sibling file is byte-equivalent
 *          to the master's; a future MR can consolidate the two by
 *          having `src/shared/reload-circuit-breaker.ts` re-export
 *          from `@quotech/auth/reload-watchdog` (zero behaviour
 *          change for the master).
 * FLOW:
 *   reload caller → recordReloadAttempt(reason) → { allowed, count, suppressed, alreadyTripped, reason }
 *
 * DEPENDENCIES:
 *   - sessionStorage (per-tab; survives reloads, isolated from siblings).
 *
 * RELATED:
 *   - ./installReloadInstrumentation.ts: wraps window.location.reload()
 *     + window.location.replace() + window.location.assign() and
 *     routes through this breaker. The tenant SPA mains invoke that.
 *   - src/shared/reload-circuit-breaker.ts: master SPA twin. Same
 *     semantics; this file's contract is identical so the master's
 *     pin tests already cover the wire shape.
 */

export const RELOAD_COUNT_KEY = 'quotech_reload_breaker_count'
export const RELOAD_WINDOW_START_KEY = 'quotech_reload_breaker_window_start'
export const RELOAD_TRIPPED_KEY = 'quotech_reload_breaker_tripped'
export const RELOAD_LAST_REASON_KEY = 'quotech_reload_breaker_last_reason'
export const RELOAD_TRAIL_KEY = 'quotech_reload_breaker_trail'

/** Threshold: 3 reloads inside the rolling window trips the breaker.
 *  The operator's symptom is "1 Hz reload"; we want to catch by the
 *  third bounce, well before the screen looks like it's strobing. */
export const RELOAD_BREAKER_THRESHOLD = 3

/** Rolling window length. Any reload outside the window resets the
 *  count to 1 (this attempt). 10 s is comfortably bigger than the
 *  150 ms setTimeout-defer used at every reload site, so a genuine
 *  loop fits multiple bounces inside the window. */
export const RELOAD_BREAKER_WINDOW_MS = 10_000

/** Max entries kept in the trail. The trail is a sliding log that
 *  records every reload attempt (suppressed or not) so the diagnostic
 *  banner can render the chain of reasons that led to the loop. A
 *  12-reload episode (the operator's complaint) fits comfortably
 *  inside 20 entries with room for context. */
export const RELOAD_TRAIL_MAX_ENTRIES = 20

export interface ReloadAttemptResult {
  allowed: boolean
  count: number
  suppressed: boolean
  alreadyTripped: boolean
  reason: string
}

export interface ReloadTrailEntry {
  at: number
  pathname: string
  reason: string
  /** Verdict outcome — `allowed`, `suppressed`, or `alreadyTripped`. */
  outcome: 'allowed' | 'suppressed' | 'alreadyTripped'
  count: number
}

function _readState(): {
  count: number
  windowStart: number
  tripped: boolean
} {
  if (typeof sessionStorage === 'undefined') {
    return { count: 0, windowStart: 0, tripped: false }
  }
  try {
    const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10)
    const windowStart = parseInt(sessionStorage.getItem(RELOAD_WINDOW_START_KEY) || '0', 10)
    const tripped = sessionStorage.getItem(RELOAD_TRIPPED_KEY) === '1'
    return {
      count: Number.isFinite(count) && count >= 0 ? count : 0,
      windowStart: Number.isFinite(windowStart) && windowStart >= 0 ? windowStart : 0,
      tripped,
    }
  } catch {
    return { count: 0, windowStart: 0, tripped: false }
  }
}

function _persist(count: number, windowStart: number, tripped: boolean, reason: string): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count))
    sessionStorage.setItem(RELOAD_WINDOW_START_KEY, String(windowStart))
    if (tripped) sessionStorage.setItem(RELOAD_TRIPPED_KEY, '1')
    if (reason) sessionStorage.setItem(RELOAD_LAST_REASON_KEY, reason)
  } catch { /* private mode / quota — ignore */ }
}

function _appendTrail(entry: ReloadTrailEntry): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(RELOAD_TRAIL_KEY)
    const existing: ReloadTrailEntry[] = raw ? JSON.parse(raw) : []
    const safe = Array.isArray(existing) ? existing : []
    safe.push(entry)
    while (safe.length > RELOAD_TRAIL_MAX_ENTRIES) safe.shift()
    sessionStorage.setItem(RELOAD_TRAIL_KEY, JSON.stringify(safe))
  } catch { /* ignore */ }
}

/**
 * Record a reload attempt. PURE I→O modulo sessionStorage: takes the
 * caller-supplied reason (free-form, e.g. an Error.stack), returns a
 * verdict telling the caller whether to proceed.
 *
 * `now` and `pathname` are injected so tests can use fake clocks /
 * routes without coordinating with `Date.now` / `window.location`.
 */
export function recordReloadAttempt(
  reason: string,
  now: number = Date.now(),
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
): ReloadAttemptResult {
  const safeReason = typeof reason === 'string' && reason ? reason : '<no-reason>'
  const state = _readState()

  // Window-expiry reset.
  if (state.windowStart === 0 || now - state.windowStart > RELOAD_BREAKER_WINDOW_MS) {
    _persist(1, now, false, safeReason)
    if (typeof sessionStorage !== 'undefined') {
      try { sessionStorage.removeItem(RELOAD_TRIPPED_KEY) } catch { /* ignore */ }
    }
    const verdict: ReloadAttemptResult = {
      allowed: true, count: 1, suppressed: false, alreadyTripped: false, reason: safeReason,
    }
    _appendTrail({ at: now, pathname, reason: safeReason, outcome: 'allowed', count: 1 })
    return verdict
  }

  // Inside active window, already tripped → drop quietly.
  if (state.tripped) {
    const nextCount = state.count + 1
    _persist(nextCount, state.windowStart, true, safeReason)
    const verdict: ReloadAttemptResult = {
      allowed: false, count: nextCount, suppressed: false, alreadyTripped: true, reason: safeReason,
    }
    _appendTrail({ at: now, pathname, reason: safeReason, outcome: 'alreadyTripped', count: nextCount })
    return verdict
  }

  // Inside window, not yet tripped. Increment and decide.
  const nextCount = state.count + 1
  if (nextCount > RELOAD_BREAKER_THRESHOLD) {
    _persist(nextCount, state.windowStart, true, safeReason)
    const verdict: ReloadAttemptResult = {
      allowed: false, count: nextCount, suppressed: true, alreadyTripped: false, reason: safeReason,
    }
    _appendTrail({ at: now, pathname, reason: safeReason, outcome: 'suppressed', count: nextCount })
    return verdict
  }

  _persist(nextCount, state.windowStart, false, safeReason)
  const verdict: ReloadAttemptResult = {
    allowed: true, count: nextCount, suppressed: false, alreadyTripped: false, reason: safeReason,
  }
  _appendTrail({ at: now, pathname, reason: safeReason, outcome: 'allowed', count: nextCount })
  return verdict
}

/** Operator-initiated reset. Called by the banner's "Force refresh" button. */
export function resetReloadBreaker(): {
  previousCount: number
  previousTripped: boolean
} {
  const previous = _readState()
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(RELOAD_COUNT_KEY)
      sessionStorage.removeItem(RELOAD_WINDOW_START_KEY)
      sessionStorage.removeItem(RELOAD_TRIPPED_KEY)
      sessionStorage.removeItem(RELOAD_LAST_REASON_KEY)
      sessionStorage.removeItem(RELOAD_TRAIL_KEY)
    } catch { /* ignore */ }
  }
  return {
    previousCount: previous.count,
    previousTripped: previous.tripped,
  }
}

export function getLastReloadReason(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return sessionStorage.getItem(RELOAD_LAST_REASON_KEY)
  } catch {
    return null
  }
}

export function isReloadBreakerTripped(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(RELOAD_TRIPPED_KEY) === '1'
  } catch {
    return false
  }
}

/** Read the full reload trail. The diagnostic banner uses this to
 *  render the chain of triggers that led to the loop so support can
 *  see whether the loop was identity-mismatch, version-watcher,
 *  notification-bell-cta, etc. */
export function getReloadTrail(): ReloadTrailEntry[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(RELOAD_TRAIL_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}
